import { useState } from 'react';
import type { CatalogChoice } from '../catalog-languages';
import { CATALOG_LANGUAGES, MAIN_LANGUAGES } from '../catalog-languages';
import { languageName, t, useLanguage } from '../i18n';

/**
 * Which languages the viewer watches: only those are loaded into their
 * channel list (several times less than the provider's whole catalog), so
 * it loads quickly on phones and TVs. Pre-filled from their country.
 */
export function LanguagePicker({
  initial,
  busy,
  submitLabel,
  onSubmit,
}: {
  initial: CatalogChoice;
  busy: boolean;
  submitLabel: string;
  onSubmit: (choice: CatalogChoice) => void;
}) {
  useLanguage();
  const [languages, setLanguages] = useState<string[]>(initial.languages);
  const [international, setInternational] = useState(initial.international);
  // The others are behind "More languages", unless one is already chosen.
  const [showAll, setShowAll] = useState(() => initial.languages.some((language) => !MAIN_LANGUAGES.includes(language)));
  const shown = showAll ? CATALOG_LANGUAGES : MAIN_LANGUAGES;
  const toggle = (language: string) =>
    setLanguages((current) => (current.includes(language) ? current.filter((chosen) => chosen !== language) : [...current, language]));

  return (
    <form
      className="language-picker"
      onSubmit={(event) => {
        event.preventDefault();
        if (languages.length > 0) onSubmit({ languages, international });
      }}
    >
      <div className="language-picker__grid" role="group" aria-label={t('catalog.languages')}>
        {shown.map((language) => (
          <button key={language} type="button" className="language-chip" aria-pressed={languages.includes(language)} onClick={() => toggle(language)}>
            {languages.includes(language) ? '✓ ' : ''}
            {languageName(language)}
          </button>
        ))}
        {showAll ? null : (
          <button type="button" className="language-chip language-chip--more" onClick={() => setShowAll(true)}>
            {t('catalog.more')}
          </button>
        )}
      </div>
      <label className="language-picker__option">
        <input type="checkbox" checked={international} onChange={(event) => setInternational(event.target.checked)} />
        <span>
          {t('catalog.international')} <span className="muted">{t('catalog.internationalHint')}</span>
        </span>
      </label>
      <p className="muted language-picker__note">
        {t('catalog.note')}
      </p>
      <button type="submit" className="button primary" disabled={busy || languages.length === 0}>
        {busy ? t('catalog.saving') : languages.length === 0 ? t('catalog.chooseOne') : submitLabel}
      </button>
    </form>
  );
}
